import { HttpEvent, HttpHandlerFn, HttpRequest } from '@angular/common/http';
import { inject, Injector } from '@angular/core';
import { catchError, EMPTY, finalize, Observable, share, switchMap, throwError } from 'rxjs';

import { RequestError } from '../../models';
import { ErrorInterceptorResult, ErrorsService, isHttpError } from '../../services/errors';

const PENDING_RESOLUTIONS = new Map<string, Observable<ErrorInterceptorResult>>();

function handleError(errorsService: ErrorsService, error: RequestError): Observable<ErrorInterceptorResult> {
  if (isHttpError(error)) {
    return errorsService.interceptError(error);
  }

  const key = `${error.errorCode}`;
  const pending$ = PENDING_RESOLUTIONS.get(key);

  if (pending$) {
    return pending$;
  }

  const resolution$ = errorsService.interceptError(error).pipe(
    finalize(() => {
      PENDING_RESOLUTIONS.delete(key);
    }),
    share(),
  );

  PENDING_RESOLUTIONS.set(key, resolution$);

  return resolution$;
}

export function handleErrorInterceptor(request: HttpRequest<unknown>, next: HttpHandlerFn): Observable<HttpEvent<unknown>> {
  const injector = inject(Injector);

  return next(request).pipe(
    catchError((error: unknown) => {
      return handleError(injector.get(ErrorsService), error as RequestError).pipe(
        switchMap((result) => {
          switch (result) {
            case 'retry':
              return next(request);
            case 'complete':
              return EMPTY;
            case 'throw':
            default:
              return throwError(() => error);
          }
        }),
      );
    }),
  );
}
